import { useCallback, useState } from "react";

import {
  ExportAssetsResponse,
  ExportLocation,
  GetExportLocationsResponse,
} from "@/components/iconik/types";

import { useApi } from "../useApi";

/**
 * Data access for the Clip Preview "Export" action.
 *
 * - ``getLocations`` loads the approved destinations the user may pick
 *   (GET /export-locations).
 * - ``exportAssets`` submits an export of one or more assets to a chosen
 *   destination (POST /export). The backend authorizes the destination and
 *   queues the work asynchronously in Iconik, so a resolved promise means
 *   "submitted", not "delivered".
 */
const useExportLocations = () => {
  const [locations, setLocations] = useState<ExportLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { apiGet, apiPost } = useApi();

  const getLocations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiGet<GetExportLocationsResponse>(
        "/export-locations"
      );
      setLocations(response?.locations ?? []);
    } catch (e) {
      console.error(e);
      setError("Failed to load export locations");
    } finally {
      setLoading(false);
    }
  }, [apiGet]);

  const exportAssets = useCallback(
    async (locationId: string, assetIds: string[]): Promise<void> => {
      const response = await apiPost<ExportAssetsResponse>("/export", {
        locationId,
        assetIds,
      });
      if (!response || response.status !== "submitted") {
        throw new Error("export request failed");
      }
    },
    [apiPost]
  );

  return { locations, loading, error, getLocations, exportAssets };
};

export default useExportLocations;
